/**
 * Host-keyed frontend tool dispatcher.
 *
 * The chat layer (useChat) calls `runHandler(host, handler, args, timeoutMs)`
 * when the backend emits a frontend_tool_call event. This module looks up
 * the right channel's handlers map based on `host` and executes the matching
 * function with a timeout race. Used by both the resume loop and the pill
 * click path.
 *
 * Channel-specific handler maps live under `./<channel>/`. Adding a new host
 * = export a `handlers` Record from that channel's folder and extend
 * `getHandlersForHost` below.
 */

import type { Channel } from "../../src/shared/host";
import { handlers as outlookHandlers } from "./outlook";
import { handlers as wordHandlers } from "./word";
import { handlers as deckorateHandlers, type HandlerContext as DeckorateHandlerContext } from "./deckorate";

export interface ResumeResult {
  ok: boolean;
  data?: Record<string, unknown>;
  error?: string;
}

/** Optional per-call context threaded through to handlers that need it — e.g.
 *  Deckorate's speaker-notes generation calls the separate Deckorate content
 *  backend and needs a bearer token. Pure Office.js handlers ignore it. */
export type HandlerContext = DeckorateHandlerContext;

type GenericHandlerFn = (args: Record<string, unknown>, ctx?: HandlerContext) => Promise<unknown>;
type GenericHandlerMap = Record<string, GenericHandlerFn>;

function getHandlersForHost(host: Channel): GenericHandlerMap {
  if (host === "word") return wordHandlers;
  if (host === "powerpoint") return deckorateHandlers;
  return outlookHandlers;
}

export async function runHandler(
  host: Channel,
  handler: string,
  args: Record<string, unknown>,
  timeoutMs: number,
  ctx?: HandlerContext,
): Promise<ResumeResult> {
  const handlers = getHandlersForHost(host);
  const fn = handlers[handler];
  if (!fn) {
    return { ok: false, error: `no handler registered for ${handler} (host=${host})` };
  }

  try {
    const data = await Promise.race([
      fn(args, ctx),
      new Promise<unknown>((_, reject) =>
        setTimeout(
          () => reject(new Error(`client tool '${handler}' timed out after ${timeoutMs}ms`)),
          Math.max(1, timeoutMs),
        ),
      ),
    ]);
    return { ok: true, data: (data ?? {}) as Record<string, unknown> };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, error: message };
  }
}
